import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { Pool } from '../types/domain';
import {
  buildCentralMonitoringPopupHtml,
  buildCctvRotationPopupHtml,
  buildPoolGridEntry,
  buildRotationSlides,
} from '../components/monitoring/monitoringPopupHtml';
import {
  readMonitoringPoolGridRotationIntervalSec,
  readMonitoringRotationIntervalSec,
  writeMonitoringPoolGridRotationIntervalSec,
} from '../components/monitoring/monitoringRotationInterval';
import {
  buildCctvPoolGridPopupPath,
  shortPathLabel,
} from '../components/monitoring/cctvPopupRoutes';
import {
  applyMonitoringPopupDocumentChrome,
  useCctvPopupCloseMessage,
} from '../components/monitoring/useCctvPopupCloseMessage';
import { useMonitoringPopupSession } from '../hooks/useMonitoringPopupSession';
import { useMonitoringPopupSnapshotBridge } from '../hooks/useMonitoringPopupSnapshotBridge';
import { useMonitoringPopupIframeKeyboard } from '../hooks/useMonitoringPopupIframeKeyboard';
import { useCentralAiOpsPresence } from '../hooks/useCentralAiOpsPresence';
import { setMonitoringPopupShowBboxPref } from '../components/monitoring/monitoringPopupBboxScript';
import { API_ORIGIN } from '../config';
import {
  fetchMonitoringPlaybackRemote,
  fetchMonitoringPoolsList,
  fetchPoolCctvsForMonitoring,
  loadPoolByCode,
} from '../components/monitoring/monitoringPopupData';
import { useAuth } from '../contexts/AuthContext';
import { isPoolSiteStandaloneView } from '../utils/poolSitePaths';

type PoolPopupMode = 'grid' | 'rotation';

type PoolSlide = {
  code: string;
  name: string;
  id?: number;
};

function clampIntervalSec(n: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(300, Math.max(1, Math.floor(n)));
}

const CctvPoolMonitoringPopupPage: React.FC = () => {
  useCctvPopupCloseMessage();
  useMonitoringPopupSession();
  // 시설 전체화면/순환도 서버 AI 스냅샷이 필요 — presence 없으면 검출 0건
  useCentralAiOpsPresence(true);
  const navigate = useNavigate();
  const { poolCode, mode: modeParam } = useParams<{ poolCode: string; mode: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { authState } = useAuth();
  const isAdminLoggedIn = Boolean(
    authState.isAdminPasswordVerified && authState.isAuthenticated
  );
  const applySiteLocal = isPoolSiteStandaloneView(location.pathname, isAdminLoggedIn);
  const mode: PoolPopupMode = modeParam === 'rotation' ? 'rotation' : 'grid';

  const intervalSec = useMemo(() => {
    const raw = parseInt(searchParams.get('interval') || '', 10);
    const fallback =
      modeParam === 'rotation'
        ? readMonitoringRotationIntervalSec()
        : readMonitoringPoolGridRotationIntervalSec();
    if (!Number.isFinite(raw)) return fallback;
    return clampIntervalSec(raw, fallback);
  }, [searchParams, modeParam]);

  const initialCctvId = useMemo(() => {
    const raw = parseInt(searchParams.get('cctvId') || '', 10);
    return Number.isFinite(raw) ? raw : undefined;
  }, [searchParams]);

  const startRotationPaused = useMemo(() => {
    const p = searchParams.get('paused');
    return p === '1' || p === 'true';
  }, [searchParams]);

  const inplaceFullscreen = useMemo(
    () => searchParams.get('inplace') === '1',
    [searchParams]
  );

  const returnToPath = useMemo(() => {
    const raw = searchParams.get('returnTo');
    return raw && raw.startsWith('/') ? raw : null;
  }, [searchParams]);

  const poolCodesFromQuery = useMemo(() => {
    const raw = searchParams.get('poolCodes') || searchParams.get('poolIds') || '';
    if (!raw.trim()) return [] as string[];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [searchParams]);

  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [operatingPoolIds, setOperatingPoolIds] = useState<number[]>([]);
  const [poolSlides, setPoolSlides] = useState<PoolSlide[]>([]);
  const [currentPoolName, setCurrentPoolName] = useState('');
  const [rollingPaused, setRollingPaused] = useState(startRotationPaused);
  const [intervalInput, setIntervalInput] = useState(String(intervalSec));
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const rollingPausedRef = useRef(rollingPaused);
  const intervalSecRef = useRef(intervalSec);

  useEffect(() => {
    rollingPausedRef.current = rollingPaused;
  }, [rollingPaused]);

  useEffect(() => {
    intervalSecRef.current = intervalSec;
    setIntervalInput(String(intervalSec));
  }, [intervalSec]);

  useEffect(() => {
    setRollingPaused(startRotationPaused);
  }, [startRotationPaused, poolCode]);

  useEffect(() => {
    setIframeLoaded(false);
  }, [html]);

  // 동일 창 전체화면: 브라우저 Fullscreen API (시설 전환마다 재요청하지 않음)
  useEffect(() => {
    if (!inplaceFullscreen || mode !== 'grid') return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    if (doc.fullscreenElement || doc.webkitFullscreenElement) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!req) return;
    const t = window.setTimeout(() => {
      req.call(el).catch(() => {});
    }, 200);
    return () => window.clearTimeout(t);
  }, [inplaceFullscreen, mode]);

  useMonitoringPopupSnapshotBridge(
    iframeRef,
    Boolean(html) && iframeLoaded,
    operatingPoolIds
  );

  useMonitoringPopupIframeKeyboard(iframeRef, Boolean(html) && iframeLoaded, {
    forwardRotationKeys: mode === 'rotation',
  });

  useEffect(() => {
    // 시설 전체화면 진입 시 검출 박스 기본 ON (끄려면 툴바 체크 해제)
    setMonitoringPopupShowBboxPref(true);
  }, []);

  useEffect(() => {
    const restoreChrome = applyMonitoringPopupDocumentChrome('#000');
    if (poolCode && modeParam) {
      document.title = shortPathLabel(location.pathname, location.search);
    }
    return restoreChrome;
  }, [poolCode, modeParam, location.pathname, location.search]);

  const currentPoolIndex = useMemo(() => {
    if (!poolCode || poolSlides.length === 0) return -1;
    const code = decodeURIComponent(poolCode);
    return poolSlides.findIndex(
      (p) => p.code.toLowerCase() === code.toLowerCase()
    );
  }, [poolCode, poolSlides]);

  const goToPoolOffset = useCallback(
    (delta: number, pauseAfter: boolean) => {
      if (poolSlides.length < 2 || currentPoolIndex < 0) return;
      const nextIdx =
        (currentPoolIndex + delta + poolSlides.length) % poolSlides.length;
      const next = poolSlides[nextIdx];
      if (!next?.code) return;
      const codes = poolSlides.map((p) => p.code);
      const path = buildCctvPoolGridPopupPath(next.code, {
        poolCodes: codes,
        intervalSec: intervalSecRef.current,
        startPaused: pauseAfter ? true : rollingPausedRef.current,
        inplace: inplaceFullscreen,
        returnTo: returnToPath,
      });
      navigate(path);
    },
    [
      poolSlides,
      currentPoolIndex,
      navigate,
      inplaceFullscreen,
      returnToPath,
    ]
  );

  // Auto pool rolling (grid only)
  useEffect(() => {
    if (mode !== 'grid') return;
    if (poolSlides.length < 2) return;
    if (rollingPaused) return;
    if (currentPoolIndex < 0) return;

    const ms =
      clampIntervalSec(intervalSec, readMonitoringPoolGridRotationIntervalSec()) *
      1000;
    const t = window.setInterval(() => {
      goToPoolOffset(1, false);
    }, ms);
    return () => window.clearInterval(t);
  }, [
    mode,
    poolSlides.length,
    rollingPaused,
    currentPoolIndex,
    intervalSec,
    goToPoolOffset,
  ]);

  // Keyboard: ←/→/Space (grid pool rolling) — same as CCTV rotation
  useEffect(() => {
    if (mode !== 'grid') return;
    if (poolSlides.length < 2) return;

    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (ev.key === ' ' || ev.key === 'Spacebar') {
        ev.preventDefault();
        setRollingPaused((p) => !p);
        return;
      }
      if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        goToPoolOffset(-1, true);
        return;
      }
      if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        goToPoolOffset(1, true);
      }
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [mode, poolSlides.length, goToPoolOffset]);

  const applyInterval = () => {
    const sec = clampIntervalSec(
      Number(intervalInput),
      readMonitoringPoolGridRotationIntervalSec()
    );
    setIntervalInput(String(sec));
    writeMonitoringPoolGridRotationIntervalSec(sec);
    if (!poolCode) return;
    const codes =
      poolSlides.length > 0
        ? poolSlides.map((p) => p.code)
        : poolCodesFromQuery;
    const path = buildCctvPoolGridPopupPath(decodeURIComponent(poolCode), {
      poolCodes: codes.length > 0 ? codes : undefined,
      intervalSec: sec,
      startPaused: rollingPaused,
      inplace: inplaceFullscreen,
      returnTo: returnToPath,
    });
    navigate(path, { replace: true });
  };

  useEffect(() => {
    if (!poolCode) {
      setError('시설 코드 없음');
      return;
    }

    let cancelled = false;

    const run = async () => {
      setHtml(null);
      setError(null);
      try {
        const [useRemotePlayback, pool, allOperatingPools] = await Promise.all([
          fetchMonitoringPlaybackRemote(),
          loadPoolByCode(poolCode),
          fetchMonitoringPoolsList(),
        ]);

        if (cancelled) return;

        if (!pool?.id) {
          setError('시설 정보를 불러오기 불가');
          return;
        }

        setOperatingPoolIds(
          allOperatingPools
            .map((p) => Number(p.id))
            .filter((id) => Number.isFinite(id))
        );
        setCurrentPoolName(pool.name || pool.code || '');

        // Build ordered pool slides for grid rolling (only when poolCodes given)
        if (mode === 'grid') {
          let slides: PoolSlide[] = [];
          if (poolCodesFromQuery.length > 0) {
            const byCode = new Map(
              allOperatingPools.map((p) => [
                String(p.code || '').toLowerCase(),
                p,
              ])
            );
            const byId = new Map(
              allOperatingPools.map((p) => [String(p.id), p])
            );
            for (const token of poolCodesFromQuery) {
              const p =
                byCode.get(token.toLowerCase()) || byId.get(token) || null;
              if (p?.code) {
                slides.push({
                  code: String(p.code),
                  name: p.name || String(p.code),
                  id: Number(p.id),
                });
              } else if (!/^\d+$/.test(token)) {
                // code not in operating list — still keep for navigation attempt
                slides.push({ code: token, name: token });
              }
            }
          }
          // No poolCodes → current pool only (do not auto-roll all operating pools)
          if (slides.length === 0 && pool.code) {
            slides = [
              {
                code: String(pool.code),
                name: pool.name || String(pool.code),
                id: Number(pool.id),
              },
            ];
          } else if (pool.code) {
            const cur = String(pool.code || '').toLowerCase();
            if (cur && !slides.some((s) => s.code.toLowerCase() === cur)) {
              slides = [
                {
                  code: String(pool.code),
                  name: pool.name || String(pool.code),
                  id: Number(pool.id),
                },
                ...slides,
              ];
            }
          }
          setPoolSlides(slides);
        } else {
          setPoolSlides([]);
        }

        const cctvs = await fetchPoolCctvsForMonitoring(pool.id);
        if (cancelled) return;

        const siteGw = Boolean(
          (pool as Pool & { site_gateway_enabled?: boolean }).site_gateway_enabled
        );
        const preferLocal = Boolean(
          (pool as Pool & { site_monitor_prefer_local?: boolean })
            .site_monitor_prefer_local
        );

        const monitoringPool = {
          id: pool.id,
          code: pool.code,
          name: pool.name,
          status: pool.status,
          site_gateway_enabled: siteGw,
          site_monitor_prefer_local: preferLocal,
        };

        if (mode === 'rotation') {
          const gridEntry = buildPoolGridEntry(monitoringPool, cctvs, {
            skipPurposeFilter: true,
          });
          const slides = buildRotationSlides(gridEntry, useRemotePlayback, {
            applySiteLocal,
          });
          if (slides.length === 0) {
            setError('순환 재생할 CCTV가 없음');
            return;
          }
          const content = buildCctvRotationPopupHtml({
            title: `${pool.name} CCTV 순환`,
            poolName: pool.name,
            poolId: pool.id,
            slides,
            intervalSec,
            initialCctvId,
            startRotationPaused,
            apiOrigin: API_ORIGIN,
            closeButtonLabel: inplaceFullscreen ? '돌아가기' : '창 닫기',
          });
          document.title = shortPathLabel(location.pathname, location.search);
          setHtml(content);
          return;
        }

        const gridEntry = buildPoolGridEntry(monitoringPool, cctvs, {
          skipPurposeFilter: true,
        });
        if (gridEntry.cctvs.length === 0) {
          setError('표시할 관제 CCTV가 없음');
          return;
        }

        const content = buildCentralMonitoringPopupHtml({
          title: `${pool.name} CCTV 모니터링`,
          poolsPerRow: 1,
          poolGridsInfo: [gridEntry],
          useRemotePlayback,
          enablePoolHeaderClick: false,
          // 시설 롤링 전용 창 — CCTV 개별 순환(클릭/툴바)과 분리
          showSinglePoolRotationInToolbar: false,
          enablePoolRotationButton: false,
          enableCctvClickRotation: false,
          autoRequestFullscreen: false,
          rotationIntervalSec: intervalSec,
          statusBarExtra: `${pool.name} · ${gridEntry.cctvs.length}개 CCTV`,
          apiOrigin: API_ORIGIN,
          closeButtonLabel: inplaceFullscreen ? '돌아가기' : '창 닫기',
          applySiteLocal,
        });
        document.title = shortPathLabel(location.pathname, location.search);
        setHtml(content);
      } catch (e) {
        console.error('CctvPoolMonitoringPopupPage load failed:', e);
        if (!cancelled) {
          const msg =
            e instanceof Error && e.message
              ? e.message
              : '모니터링 화면을 불러오기 실패';
          setError(msg);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    poolCode,
    mode,
    intervalSec,
    initialCctvId,
    startRotationPaused,
    location.pathname,
    location.search,
    poolCodesFromQuery,
    inplaceFullscreen,
    applySiteLocal,
  ]);

  const multiPool = mode === 'grid' && poolSlides.length > 1;
  const posLabel =
    multiPool && currentPoolIndex >= 0
      ? `${currentPoolIndex + 1} / ${poolSlides.length}`
      : '';

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#000',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Typography>{error}</Typography>
      </Box>
    );
  }

  if (!html) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress color="inherit" sx={{ color: '#888' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        bgcolor: '#000',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {multiPool && (
        <Box
          sx={{
            flexShrink: 0,
            zIndex: 30,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            bgcolor: 'rgba(26,26,26,0.96)',
            borderBottom: '1px solid #444',
            color: '#eee',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
            {currentPoolName || poolCode}
            {posLabel ? ` · ${posLabel}` : ''}
            {rollingPaused ? ' · 순환 일시정지' : ''}
          </Typography>
          <Typography variant="caption" color="grey.400" sx={{ mr: 1 }}>
            시설 순환
          </Typography>
          <TextField
            size="small"
            label="순환(초)"
            type="number"
            value={intervalInput}
            onChange={(e) => setIntervalInput(e.target.value)}
            inputProps={{ min: 1, max: 300, step: 1 }}
            sx={{
              width: 88,
              '& .MuiInputBase-root': { color: '#fff', bgcolor: '#333' },
              '& .MuiInputLabel-root': { color: '#bbb' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
            }}
          />
          <Button size="small" variant="outlined" onClick={applyInterval} sx={{ color: '#fff', borderColor: '#888' }}>
            적용
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRollingPaused((p) => !p)}
            sx={{ color: '#ffb74d', borderColor: '#ff9800' }}
          >
            {rollingPaused ? '순환 재개' : '순환 일시정지'}
          </Button>
          <Typography variant="caption" color="grey.500" sx={{ ml: 'auto' }}>
            ←/→ 이전·다음 · Space 정지/재개
          </Typography>
        </Box>
      )}

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        {multiPool && (
          <>
            <IconButton
              aria-label="이전 시설"
              title="이전 시설 (←)"
              onClick={() => goToPoolOffset(-1, true)}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 25,
                width: 44,
                height: 72,
                borderRadius: 1,
                bgcolor: 'rgba(20,20,20,0.55)',
                color: '#fff',
                border: '1px solid rgba(102,102,102,0.85)',
                '&:hover': { bgcolor: 'rgba(40,40,40,0.82)' },
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
            <IconButton
              aria-label="다음 시설"
              title="다음 시설 (→)"
              onClick={() => goToPoolOffset(1, true)}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 25,
                width: 44,
                height: 72,
                borderRadius: 1,
                bgcolor: 'rgba(20,20,20,0.55)',
                color: '#fff',
                border: '1px solid rgba(102,102,102,0.85)',
                '&:hover': { bgcolor: 'rgba(40,40,40,0.82)' },
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          </>
        )}

        <iframe
          ref={iframeRef}
          title="CCTV 모니터링"
          srcDoc={html}
          tabIndex={0}
          onLoad={() => setIframeLoaded(true)}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-fullscreen"
          style={{
            border: 'none',
            display: 'block',
            width: '100%',
            height: '100%',
            background: '#000',
          }}
        />
      </Box>
    </Box>
  );
};

export default CctvPoolMonitoringPopupPage;
